import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Check, Package, TrendingUp, Sparkles, Shield, Award, Users, CheckCircle, X } from 'lucide-react';
import { initiatePayment } from '../utils/razorpay';
import Footer from '../components/Footer';
import '../styles/Landing.css';

const Landing = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [showBottomBar, setShowBottomBar] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Show bottom bar after delay
    const timer = setTimeout(() => {
      setShowBottomBar(true);
    }, 1500);

    // Scroll reveal observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleOrderClick = () => {
    setShowAddressModal(true);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!addressForm.name.trim()) errors.name = 'Name is required';
    if (!addressForm.phone.trim()) errors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(addressForm.phone)) errors.phone = 'Enter valid 10-digit phone';
    if (!addressForm.address.trim()) errors.address = 'Address is required';
    if (!addressForm.city.trim()) errors.city = 'City is required';
    if (!addressForm.state.trim()) errors.state = 'State is required';
    if (!addressForm.pincode.trim()) errors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(addressForm.pincode)) errors.pincode = 'Enter valid 6-digit pincode';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAddress = async () => {
    if (!validateForm()) return;

    try {
      // Save address to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/orders/address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addressForm)
      });

      // Close modal
      setShowAddressModal(false);

      // Initiate payment with prefilled details
      await initiatePayment(499, addressForm);
      
      // Reset form
      setAddressForm({
        name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        pincode: ''
      });
    } catch (error) {
      console.error('Error processing order:', error);
      alert('Failed to process order. Please try again.');
    }
  };

  return (
    <div className="landing-container">
      {/* Noise overlay */}
      <div className="noise-overlay"></div>
      
      {/* Background glows */}
      <div className="bg-glow glow-1"></div>
      <div className="bg-glow glow-2"></div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-top">
          {/* CSS Cigarette Animation - Centered */}
          <div className="cigarette-container">
            <div className="cigarette">
              <div className="cigarette-filter"></div>
              <div className="cigarette-body"></div>
              <div className="cigarette-lit"></div>
              <div className="smoke smoke-1"></div>
              <div className="smoke smoke-2"></div>
              <div className="smoke smoke-3"></div>
            </div>
          </div>
        </div>

        <div className="hero-content">
          <h1 className="hero-heading">
            <span className="hero-line-1">Break the</span>
            <span className="hero-line-2">Habit,</span>
            <span className="hero-line-3">Not You.</span>
          </h1>
          
          <p className="hero-subtext">
            QuitStick replaces the ritual — the hold, the inhale, the moment — without a single trace of nicotine or tobacco.
          </p>
          
          <div className="hero-cta">
            <button className="cta-button" onClick={handleOrderClick}>
              <span className="cta-main-text">Get QuitStick — ₹499</span>
              <span className="cta-sub-text">Pack of 5</span>
            </button>
          </div>
          
          {/* Trust Signals */}
          <div className="hero-trust-signals">
            <div className="trust-signal">
              <Users size={16} />
              <span>10,000+ Indians quit successfully</span>
            </div>
            <div className="trust-signal">
              <Shield size={16} />
              <span>30-Day Money-Back Guarantee</span>
            </div>
          </div>
          
          {/* Social Proof Urgency */}
          <div className="social-proof-badge">
            <TrendingUp size={14} />
            <span>47 orders in the last 24 hours</span>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section reveal">
        <div className="how-it-works-steps">
          <div className="step-item">
            <div className="step-icon">
              <Package size={24} />
            </div>
            <div className="step-number">1</div>
            <div className="step-title">Order arrives in 2-3 days</div>
          </div>
          
          <div className="step-item">
            <div className="step-icon step-icon-cigarette">
              <div className="mini-cigarette">
                <div className="mini-cigarette-filter"></div>
                <div className="mini-cigarette-body"></div>
                <div className="mini-cigarette-lit"></div>
                <div className="mini-smoke mini-smoke-1"></div>
                <div className="mini-smoke mini-smoke-2"></div>
              </div>
            </div>
            <div className="step-number">2</div>
            <div className="step-title">Use when cravings hit</div>
          </div>
          
          <div className="step-item">
            <div className="step-icon">
              <Sparkles size={24} />
            </div>
            <div className="step-number">3</div>
            <div className="step-title">Break the habit naturally</div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="stats-strip reveal">
        <div className="stat-item">
          <div className="stat-number">0%</div>
          <div className="stat-label">NICOTINE</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">0%</div>
          <div className="stat-label">TOBACCO</div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="section problem-section reveal">
        <div className="section-tag">THE PROBLEM</div>
        <h2 className="section-heading">
          Smoking isn't just a <span className="highlight-text">chemical addiction.</span>
        </h2>
        
        <div className="problem-text">
          <p>It's a deeply ingrained behavioral pattern. Your brain has spent years — maybe decades — associating the act of smoking with stress relief, breaks, and moments of peace.</p>
          <p>Quitting nicotine is one thing. But what replaces the ritual? The hand-to-mouth motion? The deliberate pause? That's where most people fail.</p>
        </div>

        <div className="habit-cards">
          <div className="habit-card">
            <div className="habit-icon">🤌</div>
            <div className="habit-content">
              <div className="habit-title">The hand-to-mouth motion</div>
              <div className="habit-subtitle">Deeply ingrained motor habit</div>
            </div>
          </div>
          
          <div className="habit-card">
            <div className="habit-icon">💨</div>
            <div className="habit-content">
              <div className="habit-title">The deliberate inhale</div>
              <div className="habit-subtitle">Triggers a calming breath response</div>
            </div>
          </div>
          
          <div className="habit-card">
            <div className="habit-icon">⏸️</div>
            <div className="habit-content">
              <div className="habit-title">The break ritual</div>
              <div className="habit-subtitle">Your brain's stress-relief anchor</div>
            </div>
          </div>
          
          <div className="habit-card">
            <div className="habit-icon">🔥</div>
            <div className="habit-content">
              <div className="habit-title">The feel of a cigarette</div>
              <div className="habit-subtitle">Tactile comfort and familiarity</div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary CTA Section */}
      <section className="section secondary-cta-section reveal">
        <h3 className="secondary-cta-heading">Ready to break free?</h3>
        <p className="secondary-cta-text">Join 10,000+ Indians who chose the behavioral approach</p>
        <button className="cta-button secondary-cta-btn" onClick={handleOrderClick}>
          <span className="cta-main-text">Get QuitStick — ₹499</span>
          <span className="cta-sub-text">Pack of 5 · Free Shipping</span>
        </button>
      </section>

      {/* The Solution Section */}
      <section className="section solution-section reveal">
        <div className="section-tag">THE SOLUTION</div>
        <h2 className="section-heading">Everything you need. Nothing you don't.</h2>
        
        <div className="solution-features">
          <div className="feature-item">
            <div className="feature-dot"></div>
            <div className="feature-content">
              <div className="feature-title">Feels like the real thing</div>
              <div className="feature-description">Same size, weight, hand feel</div>
            </div>
          </div>
          
          <div className="feature-item">
            <div className="feature-dot"></div>
            <div className="feature-content">
              <div className="feature-title">Fully filter-fiber filled</div>
              <div className="feature-description">Satisfies inhale without smoke</div>
            </div>
          </div>
          
          <div className="feature-item">
            <div className="feature-dot"></div>
            <div className="feature-content">
              <div className="feature-title">Zero nicotine. Zero tobacco.</div>
              <div className="feature-description">No chemicals, no addiction</div>
            </div>
          </div>
          
          <div className="feature-item">
            <div className="feature-dot"></div>
            <div className="feature-content">
              <div className="feature-title">Use anywhere, anytime</div>
              <div className="feature-description">No smoke, ash, or lighter needed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="section comparison-section reveal">
        <div className="section-tag">THE SMART CHOICE</div>
        <h2 className="section-heading">
          QuitStick <span className="highlight-text">vs Others</span>
        </h2>
        
        <div className="comparison-table">
          <div className="comparison-row comparison-header">
            <div className="comparison-cell"></div>
            <div className="comparison-cell highlight-col">QuitStick</div>
            <div className="comparison-cell">Patches</div>
            <div className="comparison-cell">Gum</div>
            <div className="comparison-cell">Vaping</div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-cell label">Price</div>
            <div className="comparison-cell highlight-col">
              <Check size={18} className="check-green" />
              <span>₹499</span>
            </div>
            <div className="comparison-cell muted-cell">₹800-2000</div>
            <div className="comparison-cell muted-cell">₹600+</div>
            <div className="comparison-cell muted-cell">₹2000+</div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-cell label">Nicotine-Free</div>
            <div className="comparison-cell highlight-col">
              <Check size={18} className="check-green" />
              <span>100%</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Has nicotine</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Has nicotine</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Addictive</span>
            </div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-cell label">Use Anywhere</div>
            <div className="comparison-cell highlight-col">
              <Check size={18} className="check-green" />
              <span>Yes</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Visible</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Chewing</span>
            </div>
            <div className="comparison-cell muted-cell">
              <X size={18} className="x-red" />
              <span>Restricted</span>
            </div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-cell label">Lasts</div>
            <div className="comparison-cell highlight-col">
              <Check size={18} className="check-green" />
              <span>Lifetime</span>
            </div>
            <div className="comparison-cell muted-cell">1-2 weeks</div>
            <div className="comparison-cell muted-cell">1 week</div>
            <div className="comparison-cell muted-cell">Ongoing cost</div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section testimonials-section reveal">
        <div className="section-tag">REAL STORIES</div>
        <h2 className="section-heading">
          From people who <span className="highlight-text">actually quit.</span>
        </h2>
        
        <div className="testimonial-cards">
          <div className="testimonial-card">
            <div className="testimonial-header">
              <div className="testimonial-stars">★★★★★</div>
              <div className="verified-badge">
                <CheckCircle size={14} />
                <span>Verified Purchase</span>
              </div>
            </div>
            <p className="testimonial-quote">
              "I tried patches, gum, everything. QuitStick was the only thing that gave my hands something to do. Six months smoke-free now."
            </p>
            <div className="testimonial-author">
              <img 
                src="https://images.unsplash.com/photo-1607346256330-dee7af15f7c5" 
                alt="Rahul"
                className="testimonial-avatar"
              />
              <div className="testimonial-info">
                <div className="testimonial-name">Rahul K.</div>
                <div className="testimonial-detail">Mumbai · 6 months smoke-free</div>
              </div>
            </div>
          </div>
          
          <div className="testimonial-card">
            <div className="testimonial-header">
              <div className="testimonial-stars">★★★★★</div>
              <div className="verified-badge">
                <CheckCircle size={14} />
                <span>Verified Purchase</span>
              </div>
            </div>
            <p className="testimonial-quote">
              "It's not magic, but it works. The moment I feel a craving, I reach for QuitStick instead. It's saved me from relapsing twice already."
            </p>
            <div className="testimonial-author">
              <img 
                src="https://images.pexels.com/photos/7580940/pexels-photo-7580940.jpeg" 
                alt="Priya"
                className="testimonial-avatar"
              />
              <div className="testimonial-info">
                <div className="testimonial-name">Arjun M.</div>
                <div className="testimonial-detail">Bangalore · 3 months smoke-free</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section pricing-section reveal">
        <div className="pricing-card">
          <div className="pricing-badge">STARTER PACK</div>
          <div className="pricing-amount">₹499</div>
          <div className="pricing-subtitle">Pack of 5 QuitSticks · Free shipping across India</div>
          
          {/* Trust Badges in Pricing */}
          <div className="pricing-trust-badges">
            <div className="trust-badge-item">
              <Shield size={16} />
              <span>30-Day Guarantee</span>
            </div>
            <div className="trust-badge-item">
              <Award size={16} />
              <span>Made in India</span>
            </div>
          </div>
          
          <div className="pricing-checklist">
            <div className="checklist-item">
              <div className="check-icon">
                <Check size={14} />
              </div>
              <span>5 QuitSticks (behavioral replacement tools)</span>
            </div>
            <div className="checklist-item">
              <div className="check-icon">
                <Check size={14} />
              </div>
              <span>Zero nicotine, zero tobacco</span>
            </div>
            <div className="checklist-item">
              <div className="check-icon">
                <Check size={14} />
              </div>
              <span>Filter-fiber filled for realistic feel</span>
            </div>
            <div className="checklist-item">
              <div className="check-icon">
                <Check size={14} />
              </div>
              <span>Use anywhere — no smoke, no ash</span>
            </div>
            <div className="checklist-item">
              <div className="check-icon">
                <Check size={14} />
              </div>
              <span>Free delivery across India</span>
            </div>
          </div>
          
          <button className="pricing-cta" onClick={handleOrderClick}>
            Order Now — ₹499 →
          </button>
          
          <div className="pricing-secure">🔒 Secure checkout</div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section faq-section reveal">
        <div className="section-tag">FAQ</div>
        <h2 className="section-heading">Questions?</h2>
        
        <div className="faq-list">
          {[
            {
              question: "Is there anything harmful in QuitStick?",
              answer: "No. QuitStick contains only food-grade filter fiber. Zero nicotine, zero tobacco, zero chemicals. It's a purely mechanical tool designed to satisfy the behavioral ritual of smoking."
            },
            {
              question: "Will this actually help me quit?",
              answer: "QuitStick addresses the behavioral side of smoking — the hand-to-mouth motion, the inhale, the ritual. While it won't cure nicotine addiction alone, it gives you something to reach for when cravings hit, reducing relapse triggers significantly."
            },
            {
              question: "How many should I use per day?",
              answer: "Use QuitStick whenever you feel the urge to smoke. Most users start with 5-10 per day and gradually reduce usage as the behavioral pattern weakens. There's no limit — use as many as you need."
            },
            {
              question: "Can I combine this with patches or gum?",
              answer: "Absolutely. Many users combine QuitStick with nicotine replacement therapy (NRT) like patches or gum. QuitStick handles the behavioral habit while NRT addresses the chemical dependency."
            }
          ].map((faq, index) => (
            <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
              <button className="faq-question" onClick={() => toggleFaq(index)}>
                <span>{faq.question}</span>
                <ChevronDown className="faq-chevron" size={20} />
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Bar */}
      <div className={`sticky-bottom-bar ${showBottomBar ? 'visible' : ''}`}>
        <div className="bottom-bar-content">
          <div className="bottom-bar-left">
            <div className="bottom-bar-price">₹499</div>
            <div className="bottom-bar-detail">Pack of 5 · Free shipping</div>
          </div>
          <button className="bottom-bar-cta" onClick={handleOrderClick}>
            Buy Now →
          </button>
        </div>
      </div>

      {/* Address Collection Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Shipping Details</h2>
              <button className="modal-close" onClick={() => setShowAddressModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={addressForm.name}
                  onChange={handleAddressChange}
                  placeholder="Enter your full name"
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="error-text">{formErrors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={addressForm.phone}
                    onChange={handleAddressChange}
                    placeholder="10-digit mobile"
                    maxLength="10"
                    className={formErrors.phone ? 'error' : ''}
                  />
                  {formErrors.phone && <span className="error-text">{formErrors.phone}</span>}
                </div>

                <div className="form-group">
                  <label>Email (Optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={addressForm.email}
                    onChange={handleAddressChange}
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Full Address *</label>
                <textarea
                  name="address"
                  value={addressForm.address}
                  onChange={handleAddressChange}
                  placeholder="House no., Building, Street, Landmark"
                  rows="3"
                  className={formErrors.address ? 'error' : ''}
                />
                {formErrors.address && <span className="error-text">{formErrors.address}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={addressForm.city}
                    onChange={handleAddressChange}
                    placeholder="City"
                    className={formErrors.city ? 'error' : ''}
                  />
                  {formErrors.city && <span className="error-text">{formErrors.city}</span>}
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={addressForm.state}
                    onChange={handleAddressChange}
                    placeholder="State"
                    className={formErrors.state ? 'error' : ''}
                  />
                  {formErrors.state && <span className="error-text">{formErrors.state}</span>}
                </div>

                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={addressForm.pincode}
                    onChange={handleAddressChange}
                    placeholder="6-digit"
                    maxLength="6"
                    className={formErrors.pincode ? 'error' : ''}
                  />
                  {formErrors.pincode && <span className="error-text">{formErrors.pincode}</span>}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowAddressModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSubmitAddress}>
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
